import React, { useState, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { patientService } from '../../services/api';
import { Patient, PaginatedResponse } from '../../types';
import { Search, X, User } from 'lucide-react';

interface PatientSearchSelectProps {
  value?: number;
  onChange: (patientId: number | null) => void;
  error?: string;
  disabled?: boolean;
  placeholder?: string;
}

const PatientSearchSelect: React.FC<PatientSearchSelectProps> = ({
  value,
  onChange,
  error,
  disabled = false,
  placeholder = "Rechercher un patient..."
}) => {
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Récupérer les patients filtrés
  const { data: patientsResponse, isLoading } = useQuery<PaginatedResponse<Patient>>({
    queryKey: ['patients-search', searchTerm],
    queryFn: () => patientService.getAll({
      search: searchTerm,
      page_size: 20
    }).then(res => res.data),
    enabled: isOpen || searchTerm.length > 0
  });

  const patients = patientsResponse?.results || [];

  // Charger le patient sélectionné si value est fourni
  useEffect(() => {
    if (value && !selectedPatient) {
      patientService.getById(value).then(res => {
        setSelectedPatient(res.data);
      }).catch(err => {
        console.error('Error loading patient:', err);
      });
    }
  }, [value, selectedPatient]);

  // Fermer le dropdown au clic extérieur
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelectPatient = (patient: Patient) => {
    setSelectedPatient(patient);
    onChange(patient.id);
    setSearchTerm('');
    setIsOpen(false);
  };

  const handleClear = () => {
    setSelectedPatient(null);
    onChange(null);
    setSearchTerm('');
  };

  const handleInputFocus = () => {
    setIsOpen(true);
  };

  return (
    <div ref={wrapperRef} className="relative">
      {/* Input de recherche ou patient sélectionné */}
      {selectedPatient ? (
        <div className="w-full px-3 py-2 border border-gray-200 rounded-xl bg-white flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <User className="h-4 w-4 text-gray-400" />
            <span className="text-gray-900">
              {selectedPatient.nom} {selectedPatient.prenoms}
            </span>
            <span className="text-gray-500 text-sm">
              ({selectedPatient.age} ans)
            </span>
          </div>
          {!disabled && (
            <button
              type="button"
              onClick={handleClear}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      ) : (
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onFocus={handleInputFocus}
            placeholder={placeholder}
            disabled={disabled}
            className="w-full pl-10 pr-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 text-gray-900 disabled:bg-gray-100"
          />
        </div>
      )}

      {/* Message d'erreur */}
      {error && (
        <p className="text-red-500 text-sm mt-1">{error}</p>
      )}

      {/* Dropdown des résultats */}
      {isOpen && !selectedPatient && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg max-h-60 overflow-auto">
          {isLoading ? (
            <div className="px-4 py-3 text-center text-gray-500">
              <div className="inline-block animate-spin rounded-full h-5 w-5 border-b-2 border-blue-500 mr-2"></div>
              Recherche en cours...
            </div>
          ) : patients.length === 0 ? (
            <div className="px-4 py-3 text-center text-gray-500">
              {searchTerm ? 'Aucun patient trouvé' : 'Tapez pour rechercher un patient'}
            </div>
          ) : (
            <ul className="py-1">
              {patients.map((patient) => (
                <li key={patient.id}>
                  <button
                    type="button"
                    onClick={() => handleSelectPatient(patient)}
                    className="w-full px-4 py-2 text-left hover:bg-blue-50 focus:bg-blue-50 focus:outline-none transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-medium text-gray-900">
                          {patient.nom} {patient.prenoms}
                        </div>
                        <div className="text-sm text-gray-500">
                          {patient.telephone} • {patient.age} ans • {patient.genre === 'M' ? 'Masculin' : 'Féminin'}
                        </div>
                      </div>
                      {patient.assureur_details && (
                        <div className="ml-2 text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                          Assuré
                        </div>
                      )}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default PatientSearchSelect;
